/**
 * bestPotential — what this exact Pokémon could BECOME (2026-08-30 ask).
 *
 * A Charmander destined to be a Great League Charizard should be treated,
 * named and displayed as that Charizard: IVs carry through evolution
 * unchanged in GO, so the interesting number is the best full league rank
 * this IV triple can reach across the WHOLE evolution line, not just the
 * form sitting in the bag. This module answers that once, for every surface
 * that tells the story: the naming tokens (target species / best league /
 * best rank), and the live-scan PiP's target visual.
 *
 * Rules, shared with the detail page's PvP matrix:
 *   - Forms considered: the current species plus every downstream evolution
 *     with known base stats.
 *   - No-power-down floor: a form whose CP at the CURRENT level is already
 *     over a league's cap can never enter it, so that (form, league) pair is
 *     out. With no level to project from, the current form falls back to its
 *     actual CP; an evolution gets the benefit of the doubt.
 *   - Winner: the HIGHEST percent-of-best across every eligible (form,
 *     league); ties break on the lower full rank (1 = the best possible spread
 *     of 4096), then on the current form (no evolution errand for the same
 *     glory).
 *
 * PERCENT LEADS, RANK BREAKS TIES (2026-09-01 owner direction). It used to be
 * rank-first. Rank is not comparable across leagues the way it looks: each
 * league's 4096 ranks are just a sorted list, so how far #35 sits behind #1
 * depends entirely on how tightly that league's spreads bunch up, and it says
 * nothing about how close to the best possible spread this one is. Percent
 * answers exactly that question, which is the claim a destiny pick makes, and
 * it means the same thing in all three leagues.
 *
 * The report that prompted it was a 12/13/15 Snom whose HUD said Master while
 * the card beside it read 94.1% for Great and Ultra. Worth recording that the
 * pick itself was right and this reordering does not change it: Frosmoth is
 * 97.6% as a Master mon, the highest percent anywhere in that line, and the
 * "88.9%" on screen was the raw IV average (40/45) wearing a master-league
 * rank in a line that looked like a third league row. Two fixes came out of
 * it — this ordering, and the display, which now labels that line ML and shows
 * the winning percent in the target's own caption (lib/myPokemonPipSlots).
 *
 * Cost discipline (2026-08-31, for the My Pokémon "Destiny" sort): the
 * species-wide 4096-combo sweeps go through pvpRank's cached list-path
 * primitives (leagueFullRank / leaguePercent), so each (form, league) pair
 * is swept once per session no matter how many records ask; a record then
 * costs a few best-level solves. Results are additionally memoised per
 * (species, IVs, level, cp).
 */
import {
  leagueFullRank,
  leaguePercent,
  bestStatProductForCombo,
  LEAGUE_CAPS,
  type LeagueId,
} from "./pvpRank";
import {
  SPECIES_BASE_STATS, SPECIES_EVOLUTIONS } from "./generated/speciesBaseStats.generated";
import { isBaseStage } from "./generated/baseStageSpecies.generated";
import { evolutionChainForForm } from "./costumeEvolution";
import { computeCp } from "./ivSolver";
import { speciesName } from "@/data/speciesNames";

/** The leagues the single best standing may name: the HUD's and the naming tokens'. */
export type BestLeague = Extract<LeagueId, "great" | "ultra" | "master">;
/** The leagues the per-league sweep covers: the three above plus Little League (2026-10-12). */
export type PotentialLeague = BestLeague | "little";

export interface PotentialStanding<L extends PotentialLeague = PotentialLeague> {
  /** The target form (may be the current species itself). */
  speciesId: number;
  speciesName: string;
  isCurrentForm: boolean;
  league: L;
  /**
   * Every eligible league in which the target form has EXACTLY this
   * standing (same percent, same rank), in GL / UL / ML order; always
   * contains `league`. A species that cannot reach a league's CP cap even
   * at level 50 is scored against the same denominator in that league as
   * in the next one up, so its standing coincides (a Rattata reads the same
   * in all three; a Tyrunt in UL and ML). The HUD draws one ball per entry
   * and states the standing once (2026-09-02 owner ask).
   */
  leagues: L[];
  /** Full 1..4096 rank of this IV triple for the target form + league. */
  rank: number;
  /** Percent-of-best stat product for that (form, league). */
  percent: number | null;
  /** The level the target form wants to sit at, and its CP there. */
  targetLevel: number | null;
  targetCp: number | null;
}

/** The single best standing a Pokémon can reach: never Little League. */
export type BestPotential = PotentialStanding<BestLeague>;
/** One entry of the per-league sweep; Little League included. */
export type LeaguePotentialEntry = PotentialStanding<PotentialLeague>;

// Little League LAST (2026-10-12 owner ask: newer players enjoy it, it is
// cheap to build for): the GL / UL / ML order every reader relies on holds,
// and Little League only ever joins the per-league sweep (leaguePotentialsFor,
// the Box Cleanup keepers), never the single best standing the naming tokens
// and the HUD print (bestPotentialFor).
const LEAGUES: ReadonlyArray<PotentialLeague> = ["great", "ultra", "master", "little"];

/**
 * GO's Little Cup rule: CP 500 or under, AND the Pokémon must still be able
 * to evolve and never have evolved. So a species holds a Little League job
 * only as a base stage that has an evolution: Bulbasaur and Zorua yes,
 * Venusaur no, and Pokémon with no evolution at all no.
 */
export function isLittleCupSpecies(speciesId: number): boolean {
  return isBaseStage(speciesId) && (SPECIES_EVOLUTIONS[speciesId]?.length ?? 0) > 0;
}

/**
 * The best standing this exact Pokémon can reach in EACH league across its
 * evolution line, or null for a league no form can enter. Same sweep and the
 * same "percent leads, rank breaks ties" rule as bestPotentialFor, asked per
 * league: this is what a "one keeper per league" decision needs (Box
 * Cleanup's consolidation, 2026-09-10 owner ask), where the single overall
 * winner is not enough because a pile holds one job per league.
 */
export type LeaguePotentials = Record<PotentialLeague, LeaguePotentialEntry | null>;

const cache = new Map<string, BestPotential | null>();
const candidateCache = new Map<string, LeaguePotentialEntry[]>();
const leagueCache = new Map<string, LeaguePotentials | null>();

type PotentialInput = {
  speciesId: number | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  cp: number | null;
  /** The record's form: a costume can only evolve into the same costume
   * (2026-09-15 owner report: Raichu drawn as a WCS Pikachu's destiny). */
  formId?: string | null;
  /** The record's gender: a male Combee never becomes a Vespiquen (2026-09-20). */
  gender?: "male" | "female" | null;
  /**
   * The trainer's LOCKED destiny (the record page's PvP matrix tap,
   * 2026-09-20 owner report): when it names a (form, league) the line can
   * reach, that is the pick, whatever the automatic ordering says. The
   * owner's second-best Kyogre is their best Ultra one, so it is named and
   * drawn as Ultra, not as the Master pick its IVs would win alone.
   */
  pvpTarget?: { formSpeciesId: number; league: "great" | "ultra" | "master" } | null;
};

function cacheKey(record: PotentialInput, currentLevel: number | null): string | null {
  const { speciesId, ivAtk, ivDef, ivHp, cp } = record;
  if (speciesId == null || ivAtk == null || ivDef == null || ivHp == null) return null;
  const target = record.pvpTarget ? `${record.pvpTarget.formSpeciesId}/${record.pvpTarget.league}` : "";
  return `${speciesId}:${record.formId ?? ""}:${record.gender ?? ""}:${target}:${ivAtk}:${ivDef}:${ivHp}:${currentLevel ?? "?"}:${cp ?? "?"}`;
}

/** Every eligible (form, league) standing, form-major then GL/UL/ML. */
function candidatesFor(record: PotentialInput, currentLevel: number | null): LeaguePotentialEntry[] {
  const key = cacheKey(record, currentLevel);
  if (key == null) return [];
  const hit = candidateCache.get(key);
  if (hit) return hit;
  const { speciesId, ivAtk, ivDef, ivHp, cp } = record as {
    speciesId: number; ivAtk: number; ivDef: number; ivHp: number; cp: number | null;
  };
  const iv = { atk: ivAtk, def: ivDef, sta: ivHp };
  const forms = [speciesId, ...evolutionChainForForm(speciesId, record.formId, record.gender)]
    .map((id) => ({ id, base: baseFor(id) }))
    .filter((f): f is { id: number; base: [number, number, number] } => f.base != null);
  const candidates: LeaguePotentialEntry[] = [];
  for (const form of forms) {
    const isCurrent = form.id === speciesId;
    const base = { atk: form.base[0], def: form.base[1], sta: form.base[2] };
    for (const league of LEAGUES) {
      // Little League is the CURRENT form's job alone: evolving breaks the
      // Little Cup rule, so no destiny form can hold it.
      if (league === "little" && (!isCurrent || !isLittleCupSpecies(form.id))) continue;
      const cap = LEAGUE_CAPS[league];
      const floorCp =
        currentLevel != null
          ? computeCp(base, iv, currentLevel)
          : isCurrent
            ? cp
            : null;
      if (cap != null && floorCp != null && floorCp > cap) continue;
      const rank = leagueFullRank(league, { base, iv });
      if (rank == null) continue;
      const percent = leaguePercent(league, { base, iv });
      const solved = bestStatProductForCombo(base, iv, cap, 50);
      candidates.push({
        speciesId: form.id,
        speciesName: speciesName(form.id),
        isCurrentForm: isCurrent,
        league,
        leagues: [league],
        rank,
        percent,
        targetLevel: solved?.level ?? null,
        targetCp: solved?.cp ?? null,
      });
    }
  }
  candidateCache.set(key, candidates);
  return candidates;
}

export function leaguePotentialsFor(
  record: PotentialInput,
  currentLevel: number | null,
): LeaguePotentials | null {
  const key = cacheKey(record, currentLevel);
  if (key == null) return null;
  const hit = leagueCache.get(key);
  if (hit !== undefined) return hit;
  const candidates = candidatesFor(record, currentLevel);
  const out: LeaguePotentials = { great: null, ultra: null, master: null, little: null };
  for (const c of candidates) {
    const cur = out[c.league];
    if (cur == null || beats(c, cur)) out[c.league] = c;
  }
  const result = candidates.length === 0 ? null : out;
  leagueCache.set(key, result);
  return result;
}

function baseFor(id: number): [number, number, number] | null {
  return SPECIES_BASE_STATS[id] ?? null;
}

export function bestPotentialFor(record: PotentialInput, currentLevel: number | null): BestPotential | null {
  const key = cacheKey(record, currentLevel);
  if (key == null) return null;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;

  // The single best standing never names Little League: it is a side cup,
  // and a Bulbasaur's story on the HUD and in a nickname stays its Venusaur.
  const candidates = candidatesFor(record, currentLevel).filter(isBestLeagueEntry);
  let best: BestPotential | null = null;
  // A locked target wins outright when the line can still reach it; a
  // target the record has outgrown (over that league's cap now) falls back
  // to the automatic pick rather than naming something impossible.
  const target = record.pvpTarget ?? null;
  if (target) {
    best =
      candidates.find((c) => c.speciesId === target.formSpeciesId && c.league === target.league) ?? null;
  }
  for (const candidate of best ? [] : candidates) {
    if (best == null || beats(candidate, best)) best = candidate;
  }
  if (best != null) {
    // Every league in which the WINNING form holds exactly this standing.
    // Candidates were pushed form-major, league-minor in GL/UL/ML order, so
    // the filter preserves that order.
    const winner = best;
    best = {
      ...winner,
      leagues: candidates
        .filter(
          (c) =>
            c.speciesId === winner.speciesId &&
            c.rank === winner.rank &&
            c.percent === winner.percent,
        )
        .map((c) => c.league),
    };
  }
  cache.set(key, best);
  return best;
}

/**
 * Is `a` the better claim than `b`? Percent first, then rank, then the form
 * already in the bag.
 *
 * A missing percent sorts last rather than as 0 so it can never tie with a
 * genuine 0%: an unknown standing is not a bad one, it is no claim at all.
 */
function isBestLeagueEntry(c: LeaguePotentialEntry): c is BestPotential {
  return c.league !== "little";
}

function beats(a: LeaguePotentialEntry, b: LeaguePotentialEntry): boolean {
  const ap = a.percent ?? -1;
  const bp = b.percent ?? -1;
  if (ap !== bp) return ap > bp;
  if (a.rank !== b.rank) return a.rank < b.rank;
  return a.isCurrentForm && !b.isCurrentForm;
}

/** Superscript league mark for a nickname ("ᴳ" / "ᵁ" / "ᴹ"), decoder-known. */
export function leagueMark(league: PotentialLeague): string {
  return league === "great" ? "ᴳ" : league === "ultra" ? "ᵁ" : league === "little" ? "ᴸ" : "ᴹ";
}

/** Test seam. */
export function _resetBestPotentialCacheForTests(): void {
  cache.clear();
  candidateCache.clear();
  leagueCache.clear();
}
